import React, { Suspense, lazy, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useFeatureFlag } from "../../context/featureFlagContext";
import { useFilter } from "../../context/filterContext";
import { useSearchQuery } from "../../context/searchContext";
import getTenantIdFromURL from "../../utils/getTenantIdFromURL";
import { formatArcScore, formatCurrencyValue } from "../../utils/Helper";
import { useSelector } from "react-redux";
import FilterDialog from "../../FilterDialog";
import { 
  STRING_OPERATORS, 
  NUMBER_OPERATORS, 
  DATE_OPERATORS, 
  defaultFilters, 
  applyFilters,
  REGION_COUNTRIES
} from "./filterLogic";
import { listCompanySummaryData } from "../../api/underwritingApi";
import { parseFirestoreTimestamp } from "../../utils/timestampUtils";

const CompanySection = lazy(() => import("./CompanySection"));

const CompanyDashboard = ({ adminAction }) => {
  const { searchQuery } = useSearchQuery();
  const { isFeatureFlag } = useFeatureFlag();
  const selectedTenant = getTenantIdFromURL();
  const [tenantId, setTenantId] = useState(selectedTenant);
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenant = useSelector((state) => state.userInfo.tenantId);
  const URLTenant = getTenantIdFromURL();
  const [isCompanySummaryLoading, setIsCompanySummaryLoading] = useState(false);
  const [companySummaryData, setCompanySummaryData] = useState([]);
  const [arcScoreChangedCompanies, setArcScoreChangedCompanies] = useState([]);
  const [
    recommendedLimitChangedCompanies,
    setRecommendedLimitChangedCompanies,
  ] = useState([]);
  const [asiaCompanies, setAsiaCompanies] = useState([]);
  const [europeCompanies, setEuropeCompanies] = useState([]);
  const [usCompanies, setUsCompanies] = useState([]);
  const [africaCompanies, setAfricaCompanies] = useState([]);
  const [australiaCompanies, setAustraliaCompanies] = useState([]);
  const [middleEastCompanies, setMiddleEastCompanies] = useState([]);
  const [northAmericaCompanies, setNorthAmericaCompanies] = useState([]);
  const [centralAmericaCompanies, setCentralAmericaCompanies] = useState([]);
  const [caribbeanCompanies, setCaribbeanCompanies] = useState([]);
  const [southAmericaCompanies, setSouthAmericaCompanies] = useState([]);
  const [latamCompanies, setLatamCompanies] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const { filterOpen, handleFilterClose } = useFilter();
  const [lastVisitedCompanyIds, setLastVisitedCompanyIds] = useState([]);

  const handleApplyFilters = (newFilters) => {
    console.log('CompanyDashboard handleApplyFilters newFilters:', newFilters);
    const hasActiveFilters = Object.values(newFilters).some(filter => filter.value);

    if (hasActiveFilters) {
      setFilters(newFilters);
      const filtered = applyFilters(companySummaryData, newFilters);
      console.log('CompanyDashboard filtered results:', filtered);
      
      // Process the filtered data the same way as original data
      const processedFilteredData = filtered.map(row => ({
        ...row,
        summaryData: typeof row.summaryData === "string"
          ? JSON.parse(row.summaryData)
          : row.summaryData
      }));
      
      setFilteredCompanies(processedFilteredData);
      setIsFiltered(true);
    } else {
      setFilters(defaultFilters);
      setFilteredCompanies([]);
      setIsFiltered(false);
    }
  };

  const getRegionForCountry = (country) => {
    for (const [region, countries] of Object.entries(REGION_COUNTRIES)) {
      if (countries.includes(country)) {
        return region;
      }
    }
    return null;
  };

  const categorizeCompaniesByRegion = (companySummaries) => {
    const result = {
      asia: [],
      europe: [],
      northAmerica: [],
      centralAmerica: [],
      caribbean: [],
      southAmerica: [],
      africa: [],
      australia: [],
      middleEast: [],
      latam: [],
    };

    companySummaries.forEach((summary) => {
      const country = summary["country"] || "";
      const region = getRegionForCountry(country);
      if (!region) return;

      switch (region) {
        case "Asia":
          result.asia.push(summary);
          break;
        case "Europe":
          result.europe.push(summary);
          break;
        case "North America":
          result.northAmerica.push(summary);
          break;
        case "Central America":
          result.centralAmerica.push(summary);
          break;
        case "Caribbean":
          result.caribbean.push(summary);
          break;
        case "South America":
          result.southAmerica.push(summary);
          break;
        case "Africa":
          result.africa.push(summary);
          break;
        case "Australia and New Zealand":
          result.australia.push(summary);
          break;
        case "Middle East":
          result.middleEast.push(summary);
          break;
        case "LATAM":
          result.latam.push(summary);
          break;
        default:
          break;
      }
    });

    return result;
  };

  const getCompaniesWithRecentChanges = (companySummaryData) => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  
    const arcScoreChanged = [];
    const recommendedLimitChanged = [];
  
    companySummaryData.forEach((summary) => {
      const arcScoreUpdatedAt = parseFirestoreTimestamp(summary.arcScoreLastUpdatedAt);
      if (arcScoreUpdatedAt && arcScoreUpdatedAt >= oneMonthAgo) {
          arcScoreChanged.push(summary);
      }
  
      const recommendedLimitUpdatedAt = parseFirestoreTimestamp(summary.recommendedLimitLastUpdatedAt);
      if (recommendedLimitUpdatedAt && recommendedLimitUpdatedAt >= oneMonthAgo) {
          recommendedLimitChanged.push(summary);
      }
    });
  
    return {
      arcScoreChanged,
      recommendedLimitChanged,
    };
  };

  const isValidISOString = (dateString) => {
    if (dateString === null || dateString === undefined) {
      return false;
    }
    
    // Handle Firestore timestamp format
    if (typeof dateString === 'object' && dateString._seconds) {
      return true;
    }
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return false;
      }
      
      return Boolean(dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)?Z$/));
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (isAdmin && URLTenant) {
      setTenantId(URLTenant);
    } else if (currentTenant) {
      setTenantId(currentTenant);
    }
  }, [isAdmin, URLTenant, currentTenant]);

  // States for different company lists
  const [newCompanies, setNewCompanies] = useState([]);
  const [recentlyUpdatedCompanies, setRecentlyUpdatedCompanies] = useState([]);
  const [recentlyVisitedCompanies, setRecentlyVisitedCompanies] = useState([]);
  const [riskyCompanies, setRiskyCompanies] = useState([]);
  const [lowCreditCompanies, setLowCreditCompanies] = useState([]);
  const [lowMediumCreditCompanies, setLowMediumCreditCompanies] = useState([]);
  const [mediumCreditCompanies, setMediumCreditCompanies] = useState([]);
  const [highCreditCompanies, setHighCreditCompanies] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [alerts, setAlerts] = useState([]);

  // Fetch company summaries and companies data
  useEffect(() => {
    if (tenantId) {
      fetchCompanyData();
    }
  }, [tenantId]);

  const fetchCompanyData = async () => {
    try {
      setIsCompanySummaryLoading(true);
      
      // Fetch company summaries from underwriting service
      const summariesResponse = await listCompanySummaryData(tenantId);
      console.log('Fetched company summaries:', summariesResponse);
      
      if (summariesResponse.success && summariesResponse.data) {
        // Parse summaryData once here and filter out undefined summaryData
        const parsedData = summariesResponse.data
          .map(row => ({
            ...row,
            summaryData: typeof row.summaryData === "string"
              ? JSON.parse(row.summaryData)
              : row.summaryData
          }))
          .filter(row => row.summaryData !== undefined && row.summaryData !== null);

        setCompanySummaryData(parsedData);
      }

    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setIsCompanySummaryLoading(false);
    }
  };

  // Process company data when available
  useEffect(() => {
    if (!companySummaryData || companySummaryData.length === 0) return;

    // Risky Companies
    const allRiskyCompanies = sortARriskyRows(companySummaryData);
    setRiskyCompanies(allRiskyCompanies);

    // New Companies
    let recentCompanies = [...companySummaryData];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    recentCompanies = recentCompanies
      .filter(company => {
        const createdDate = parseFirestoreTimestamp(company.createdAt);
        return createdDate && createdDate >= sevenDaysAgo;
      })
      .sort((a, b) => {
        const dateA = parseFirestoreTimestamp(a.createdAt);
        const dateB = parseFirestoreTimestamp(b.createdAt);
        return dateB - dateA;
      });
    setNewCompanies(recentCompanies);

    // Recently Updated Companies
    const topRecentUpdatedCompanies = getRecentlyUpdatedCompanies(companySummaryData);
    setRecentlyUpdatedCompanies(topRecentUpdatedCompanies);

    // Credit-based Companies
    const creditBasedCompanies = categorizeCompaniesByCredit(companySummaryData);
    setLowCreditCompanies(creditBasedCompanies.low);
    setLowMediumCreditCompanies(creditBasedCompanies.lowMedium);
    setMediumCreditCompanies(creditBasedCompanies.medium);
    setHighCreditCompanies(creditBasedCompanies.high);

    // New processing for recent changes
    const { arcScoreChanged, recommendedLimitChanged } =
      getCompaniesWithRecentChanges(companySummaryData);
    setArcScoreChangedCompanies(arcScoreChanged);
    setRecommendedLimitChangedCompanies(recommendedLimitChanged);
 
    // Regional categorization
    const regionalCompanies = categorizeCompaniesByRegion(companySummaryData);
    setAsiaCompanies(regionalCompanies.asia);
    setEuropeCompanies(regionalCompanies.europe);
    setNorthAmericaCompanies(regionalCompanies.northAmerica);
    setCentralAmericaCompanies(regionalCompanies.centralAmerica);
    setCaribbeanCompanies(regionalCompanies.caribbean);
    setSouthAmericaCompanies(regionalCompanies.southAmerica);
    setAfricaCompanies(regionalCompanies.africa);
    setAustraliaCompanies(regionalCompanies.australia);
    setMiddleEastCompanies(regionalCompanies.middleEast);
    setLatamCompanies(regionalCompanies.latam);

    // Recently visited companies (simulated with recent companies for now)
    setRecentlyVisitedCompanies(recentCompanies.slice(0, 5));
  }, [companySummaryData]);

  const getRecentlyUpdatedCompanies = (companySummaryData) => {
    const sortedSummaries = [...companySummaryData].sort((a, b) => {
      const dateA = parseFirestoreTimestamp(a.updatedAt);
      const dateB = parseFirestoreTimestamp(b.updatedAt);
      return dateB - dateA;
    });

    return sortedSummaries;
  };

  const categorizeCompaniesByCredit = (companySummaryData) => {
    const result = {
      low: [],
      lowMedium: [],
      medium: [],
      high: [],
    };

    companySummaryData.forEach((summary) => {
      const summaryData = summary.summaryData;
      if (!summaryData) return;
      const recommendedLimit = summaryData["Recommended Limit"] || 0;

      if (recommendedLimit <= 150000) {
        result.low.push(summary);
      } else if (recommendedLimit <= 500000) {
        result.lowMedium.push(summary);
      } else if (recommendedLimit <= 1000000) {
        result.medium.push(summary);
      } else {
        result.high.push(summary);
      }
    });

    return result;
  };

  const sortARriskyRows = (data) => {
    const sortedData = data
      .filter((row) => {
        const summary = row["summaryData"];
        const finalScore = summary["Combined Score"] || 0;
        return finalScore >= 8;
      })
      .sort((a, b) => {
        const summaryA = a["summaryData"];
        const summaryB = b["summaryData"];
        const aFinalScore = formatArcScore(summaryA["Combined Score"]) || 0;
        const bFinalScore = formatArcScore(summaryB["Combined Score"]) || 0;
        return bFinalScore - aFinalScore;
      });

    return sortedData;
  };

  // Handle search
  useEffect(() => {
    if (!searchQuery) {
      setSearchResults([]);
      return;
    }

    const results = companySummaryData.filter((summary) => {
      const summaryData = summary.summaryData;
      return summaryData && summaryData["Company Name"]?.toLowerCase().includes(searchQuery.toLowerCase());
    });
    setSearchResults(results);
  }, [searchQuery, companySummaryData]);

  const handleViewAll = (sectionTitle) => {
    setExpandedSection(sectionTitle);
  };

  const handleBack = () => {
    setExpandedSection(null);
  };

  const renderSection = (title, companies, fullPage = false) => {
    if (!companies || companies.length === 0) return null;

    const isExpanded = expandedSection === title;
    const displayCompanies = isExpanded ? companies : companies.slice(0, 10);

    // If a section is expanded, only show that section
    if (expandedSection && !isExpanded) {
      return null;
    }

    return (
      <Suspense fallback={<CircularProgress />}>
        <CompanySection
          title={title}
          companies={displayCompanies}
          totalCompanies={companies.length}
          adminAction={adminAction}
          companySummaryData={companySummaryData}
          alerts={alerts}
          fullPage={fullPage}
          onViewAll={handleViewAll}
          isExpanded={isExpanded}
          onBack={handleBack}
        />
      </Suspense>
    );
  };

  if (isCompanySummaryLoading) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "400px" 
      }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <>
      <FilterDialog
        open={filterOpen}
        onClose={handleFilterClose}
        initialFilters={filters}
        onApplyFilters={handleApplyFilters}
        STRING_OPERATORS={STRING_OPERATORS}
        NUMBER_OPERATORS={NUMBER_OPERATORS}
        DATE_OPERATORS={DATE_OPERATORS}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 2 }}>
        {isFiltered ? (
          renderSection("Filtered Results", filteredCompanies, true)
        ) : searchQuery ? (
          renderSection("Search Results", searchResults, true)
        ) : (
          <>
            {newCompanies.length > 0 && 
              renderSection("New Companies", newCompanies)}
            {riskyCompanies.length > 0  &&
              renderSection("Risky Companies", riskyCompanies)}
            {highCreditCompanies.length > 0 &&
              renderSection("High Credit", highCreditCompanies)}
            {mediumCreditCompanies.length > 0 &&
              renderSection("Medium Credit", mediumCreditCompanies)}
            {lowMediumCreditCompanies.length > 0 &&
              renderSection("Low-Medium Credit", lowMediumCreditCompanies)}
            {lowCreditCompanies.length > 0 &&
              renderSection("Low Credit", lowCreditCompanies)}
            {arcScoreChangedCompanies.length > 0 &&
              renderSection("ARC Score Recently Changed", arcScoreChangedCompanies)}
            {recommendedLimitChangedCompanies.length > 0 &&
              renderSection("Recommended Limit Recently Changed", recommendedLimitChangedCompanies)}
            {asiaCompanies.length > 0 &&
              renderSection("Asia", asiaCompanies)}
            {europeCompanies.length > 0 &&
              renderSection("Europe", europeCompanies)}
            {northAmericaCompanies.length > 0 &&
              renderSection("North America", northAmericaCompanies)}
            {centralAmericaCompanies.length > 0 &&
              renderSection("Central America", centralAmericaCompanies)}
            {caribbeanCompanies.length > 0 &&
              renderSection("Caribbean", caribbeanCompanies)}
            {southAmericaCompanies.length > 0 &&
              renderSection("South America", southAmericaCompanies)}
            {africaCompanies.length > 0 &&
              renderSection("Africa", africaCompanies)}
            {australiaCompanies.length > 0 &&
              renderSection("Australia and New Zealand", australiaCompanies)}
            {middleEastCompanies.length > 0 &&
              renderSection("Middle East", middleEastCompanies)}
            {latamCompanies.length > 0 &&
              renderSection("LATAM", latamCompanies)}
            {recentlyUpdatedCompanies.length > 0  &&
              renderSection("Recently Changed", recentlyUpdatedCompanies)}
            {recentlyVisitedCompanies.length > 0  &&
              renderSection("Recently Visited", recentlyVisitedCompanies)}
          </>
        )}
      </Box>
    </>
  );
};

export default CompanyDashboard;